import React, { Suspense } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SEOMetadata from '@/components/SEOMetadata';
import Loader from '@/Loader';
import { breakpoints } from '@/layouts/breakpoints';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

interface SitemapEntry {
  path: string;
  priority: number;
  changefreq: string;
}

const Wrapper = styled.div`
  min-height: 100vh;
  background: #fff;
  padding-top: 40px;
`;

const Container = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 160px 24px 96px;

  @media (max-width: ${breakpoints.medium}) {
    padding: 80px 16px 64px;
  }
`;

const Title = styled.h1`
  font-size: 38px!important;
  color: #111;
  margin: 0 0 24px;
  letter-spacing: -0.02em;

  @media (max-width: ${breakpoints.medium}) {
    font-size: 26px!important;
  }
`;

const TitleRule = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin: 0 0 48px;
`;

const SectionsGrid = styled.div`
  column-count: 3;
  column-gap: 56px;

  @media (max-width: 900px) {
    column-count: 2;
    column-gap: 40px;
  }

  @media (max-width: ${breakpoints.medium}) {
    column-count: 1;
  }
`;

const Section = styled.div`
  break-inside: avoid;
  margin-bottom: 36px;
`;

const SectionTitle = styled.h2`
  font-size: 17px;
  font-weight: 600;
  color: #111;
  margin: 0 0 14px;
`;

const SubSection = styled.div`
  break-inside: avoid;
  margin-bottom: 36px;
`;

const DocTitle = styled.h2`
  font-size: 26px;
  color: #111;
  margin: 0 0 48px;
  letter-spacing: -0.02em;

  @media (max-width: ${breakpoints.medium}) {
    font-size: 20px;
  }
`;

const DocDivider = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin: 24px 0 36px;
`;

const DocGrid = styled.div`
  column-count: 3;
  column-gap: 56px;

  @media (max-width: 900px) {
    column-count: 2;
    column-gap: 40px;
  }

  @media (max-width: ${breakpoints.medium}) {
    column-count: 1;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ListItem = styled(Link)`
  font-size: 15px;
  color: #0066cc;
  text-decoration: none;
  line-height: 1.4;

  &:hover {
    text-decoration: underline;
  }
`;

const LoadingState = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 100px 0;
  color: #999;
  font-size: 15px;
`;

const docSectionLabels: Record<string, string> = {
  '00-overview': 'Overview',
  '01-market-data': 'Market Data',
  '02-alternative-data': 'Alternative Data',
  '03-fundamentals': 'Fundamentals',
  '04-macros-and-signals': 'Macros & Signals',
  'client-libraries': 'Client Libraries',
};

const orderedDocSections = [
  '00-overview',
  '01-market-data',
  '02-alternative-data',
  '03-fundamentals',
  '04-macros-and-signals',
  'client-libraries',
];

function labelFromPath(path: string): string {
  const parts = path.replace(/^\//, '').split('/').filter(Boolean);
  if (parts.length === 0) return 'Home';
  if (parts[0] === 'stock' && parts[1]) return parts[1].toUpperCase();
  return parts
    .map(p => p.replace(/^\d+-/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
    .join(' / ');
}

function categoryFromPath(path: string): string {
  if (path === '/') return 'Homepage';
  if (path === '/stocks') return 'Stocks';
  if (path.startsWith('/stock/')) return 'Stock Reports';
  if (path.startsWith('/products/') || path.startsWith('/product')) return 'Product Pages';
  if (path === '/compare' || path.startsWith('/compare/')) return 'Comparisons';
  if (path.startsWith('/blog/')) return 'Blog';
  if (path.startsWith('/developers') || path == '/docs' || path === '/blog') return 'Developers';
  if (path.startsWith('/dashboard')) return 'Dashboard';
  if (path.startsWith('/docs')) return 'Documentation';
  if (['/about', '/pricing', '/contact', '/jobs', '/support', '/privacy', '/terms'].includes(path)) return 'Pages';
  return 'Other';
}

function docSectionFromPath(path: string): string | null {
  const match = path.match(/^\/docs\/([^/]+)/);
  return match ? match[1] : null;
}

export default function SitemapPage() {
  useIdlePrefetch([loadGetStarted, loadFooter]);
  const [entries, setEntries] = useState<SitemapEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('/sitemap.xml')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.text();
      })
      .then(xml => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, 'text/xml');
        const urls = doc.querySelectorAll('url');
        const parsed: SitemapEntry[] = Array.from(urls).map(url => {
          const loc = url.querySelector('loc')?.textContent || '';
          const path = new URL(loc).pathname;
          const priority = parseFloat(url.querySelector('priority')?.textContent || '0');
          const changefreq = url.querySelector('changefreq')?.textContent || '';
          return { path, priority, changefreq };
        });
        parsed.sort((a, b) => b.priority - a.priority);
        setEntries(parsed);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <LoadingState>
        <Loader />
      </LoadingState>
    );
  }

  const sections = ['Homepage', 'Pages', 'Comparisons', 'Blog', 'Developers', 'Product Pages', 'Stock Reports', 'Dashboard', 'Other', 'Documentation' ] as const;
  const grouped = sections.reduce((acc, key) => {
    acc[key] = entries.filter(e => categoryFromPath(e.path) === key);
    return acc;
  }, {} as Record<string, SitemapEntry[]>);

  const docEntries = grouped['Documentation'] || [];
  const docGrouped = orderedDocSections.reduce((acc, key) => {
    const items = docEntries.filter(e => docSectionFromPath(e.path) === key);
    if (items.length > 0) acc[key] = items;
    return acc;
  }, {} as Record<string, SitemapEntry[]>);

  const blogEntries = grouped['Blog'] || [];
  const stockEntries = grouped['Stock Reports'] || [];
  const comparisonEntries = grouped['Comparisons'] || [];

  return (
    <>
      <SEOMetadata
        title="Sitemap"
        description="Complete sitemap of all pages on AxionQuant. Explore our products, documentation, and blog."
        canonicalUrl="https://axionquant.com/sitemap"
      />

      <Wrapper>
        <Container>
          <Title className="serif">AxionQuant Site Map</Title>
          <TitleRule />

          {error ? (
            <ErrorState>Could not load sitemap. Try again later.</ErrorState>
          ) : (
            <>
              <SectionsGrid>
                {sections.map(section => {
                  if (section === 'Documentation' || section === 'Blog' || section === 'Stock Reports' || section === 'Comparisons') return null;
                  const items = grouped[section];
                  if (!items || items.length === 0) return null;
                  return (
                    <Section key={section}>
                      <SectionTitle>{section}</SectionTitle>
                      <List>
                        {items.map(entry => (
                          <ListItem key={entry.path} to={entry.path}>
                            {labelFromPath(entry.path)}
                          </ListItem>
                        ))}
                      </List>
                    </Section>
                  );
                })}
              </SectionsGrid>

              {Object.keys(docGrouped).length > 0 && (
                <>
                  <DocDivider />
                  <DocTitle className="serif">Documentation Site Map</DocTitle>
                  <DocGrid>
                    {orderedDocSections.map(subKey => {
                      const items = docGrouped[subKey];
                      if (!items) return null;
                      return (
                        <SubSection key={subKey}>
                          <SectionTitle>{docSectionLabels[subKey] || subKey}</SectionTitle>
                          <List>
                            {items.map(entry => (
                              <ListItem key={entry.path} to={entry.path}>
                                {labelFromPath(entry.path)}
                              </ListItem>
                            ))}
                          </List>
                        </SubSection>
                      );
                    })}
                  </DocGrid>
                </>
              )}

              {blogEntries.length > 0 && (
                <>
                  <DocDivider />
                  <DocTitle className="serif">Blog Site Map</DocTitle>
                  <DocGrid>
                    <SubSection>
                      <List>
                        {blogEntries.map(entry => (
                          <ListItem key={entry.path} to={entry.path}>
                            {labelFromPath(entry.path)}
                          </ListItem>
                        ))}
                      </List>
                    </SubSection>
                  </DocGrid>
                </>
              )}

              {comparisonEntries.length > 0 && (
                <>
                  <DocDivider />
                  <DocTitle className="serif">Comparisons Site Map</DocTitle>
                  <DocGrid>
                    <SubSection>
                      <List>
                        {comparisonEntries.map(entry => (
                          <ListItem key={entry.path} to={entry.path}>
                            {labelFromPath(entry.path)}
                          </ListItem>
                        ))}
                      </List>
                    </SubSection>
                  </DocGrid>
                </>
              )}

              {stockEntries.length > 0 && (
                <>
                  <DocDivider />
                  <DocTitle className="serif">Stock Reports Site Map</DocTitle>
                  <DocGrid>
                    <SubSection>
                      <List>
                        {stockEntries.map(entry => (
                          <ListItem key={entry.path} to={entry.path}>
                            {labelFromPath(entry.path)}
                          </ListItem>
                        ))}
                      </List>
                    </SubSection>
                  </DocGrid>
                </>
              )}
            </>
          )}
        </Container>
      </Wrapper>
      <Suspense fallback={null}>
      <GetStarted />
      <Footer />
      </Suspense>
    </>
  );
}
